import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client'; 
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea'; 
import { useToast } from '@/components/ui/use-toast'; 
import type { StripeProduct, StripePrice } from '@/types'; 

// Define the payload structure for the new sync function
interface SyncCoursePayload {
  stripe_product_id: string;
  course_slug: string; // Needed to update the database
  name?: string; // Optional updates
  description?: string; // Optional updates
  price?: number; // Price in cents
  currency?: string; // e.g., 'usd', 'eur'
}

interface EditCourseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editingProduct: StripeProduct & { metadata?: { course_slug?: string } } | null; // Expect course_slug in metadata
}

// Helper to convert cents to display format (e.g., 1999 -> 19.99)
const centsToDisplayPrice = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
};

// Helper to convert display format to cents (e.g., 19.99 -> 1999)
const displayPriceToCents = (price: string): number | undefined => {
  const value = parseFloat(price);
  if (isNaN(value)) return undefined;
  return Math.round(value * 100);
};

const EditCourseDialog: React.FC<EditCourseDialogProps> = ({ 
  isOpen, 
  onClose, 
  editingProduct 
}) => {
  const [courseName, setCourseName] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  // State for single price and currency
  const [coursePrice, setCoursePrice] = useState('');
  const [courseCurrency, setCourseCurrency] = useState('usd'); // Default to USD

  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    if (editingProduct) {
      setCourseName(editingProduct.name || '');
      setCourseDescription(editingProduct.description || '');
      // Set price and currency from the product's default_price
      const defaultPrice = editingProduct.default_price as StripePrice | null;
      setCoursePrice(centsToDisplayPrice(defaultPrice?.unit_amount));
      setCourseCurrency(defaultPrice?.currency || 'usd'); // Fallback to usd
    } else {
      // Clear all fields if no product is being edited
      setCourseName('');
      setCourseDescription('');
      setCoursePrice('');
      setCourseCurrency('usd'); // Reset to default
    }
  }, [editingProduct, isOpen]); 

  // Update mutation to call the new sync function
  const syncMutation = useMutation<any, Error, SyncCoursePayload>({
    mutationFn: async (payload: SyncCoursePayload) => { 
      console.log('Invoking sync-course-details with:', payload); 
      // Call the NEW sync function
      const { data, error } = await supabase.functions.invoke('sync-course-details', {
        body: payload,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      toast({ title: 'Success', description: 'Course details synchronized successfully.' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stripeProducts'] }); // Re-fetch product list
      onClose(); 
    },
    onError: (error: Error) => {
      toast({ title: 'Error synchronizing course', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingProduct) return;

    // Retrieve course_slug from metadata - IMPORTANT: Ensure this exists!
    const courseSlug = editingProduct.metadata?.course_slug;
    if (!courseSlug) {
        toast({ title: 'Error', description: 'Course slug is missing in product metadata. Cannot sync.', variant: 'destructive' });
        console.error("Missing course_slug in metadata for product:", editingProduct.id);
        return; 
    }

    // Construct the new payload with single price/currency
    const payload: SyncCoursePayload = {
      stripe_product_id: editingProduct.id, 
      course_slug: courseSlug, // Pass the slug
      name: courseName, // Pass updated name/desc
      description: courseDescription,
      // Convert display price to cents, pass undefined if blank/invalid
      price: displayPriceToCents(coursePrice),
      currency: courseCurrency.toLowerCase() || undefined, // Pass currency, ensure lowercase
    };

    console.log('Submitting sync payload:', payload); 

    syncMutation.mutate(payload);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Course & Sync</DialogTitle>
          <DialogDescription>
            Update Stripe product details and set the default price.
            Enter the new price and currency below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Name Input - unchanged */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input 
                id="name" 
                value={courseName} 
                onChange={(e) => setCourseName(e.target.value)} 
                className="col-span-3" 
                required 
              />
            </div>
            {/* Description Input - unchanged */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea 
                id="description" 
                value={courseDescription} 
                onChange={(e) => setCourseDescription(e.target.value)} 
                className="col-span-3" 
              />
            </div>
            {/* --- Single Price Input --- */} 
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="price" className="text-right">
                Price
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={coursePrice}
                onChange={(e) => setCoursePrice(e.target.value)}
                className="col-span-3"
                placeholder="e.g., 19.99"
              />
            </div>
            {/* --- Currency Input --- */} 
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currency" className="text-right">
                Currency
              </Label>
              <Input
                id="currency"
                type="text"
                maxLength={3} // Standard currency codes are 3 letters
                value={courseCurrency}
                onChange={(e) => setCourseCurrency(e.target.value.toLowerCase())} // Force lowercase
                className="col-span-3"
                placeholder="e.g., usd, eur"
                required // Currency is required if price is set
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={syncMutation.isPending}>
              {syncMutation.isPending ? 'Syncing...' : 'Save & Sync'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCourseDialog;
